// AlertsAndNotifications.tsx
//
// O que esta aba mostrava até aqui era, inteiro, dado de mentira: três alertas,
// três "alertas recentes" e três templates escritos como literal dentro do JSX.
// Nada vinha do banco, nada era gravável, e os três botões que chamavam
// `handleOpenModal("alertConfig")` não abriam nada — o modal nunca existiu.
//
// O que passou a valer:
//   · configurações de alerta  -> ActSubsidiaryInfo tipo 23, lidas do infoToken
//   · templates de mensagem    -> ActOrganizationInfo tipo 20, lidos do
//                                 infoToken, com TEMPLATES_PADRAO como SEMENTE
//                                 visível enquanto não há nada gravado
//   · alertas recentes         -> FORA DE ESCOPO, e dito na tela: histórico de
//                                 alerta depende de uma entidade "Alerta" que
//                                 não existe (nenhuma tabela, nenhuma rota).
//                                 Inventar linha aqui seria a mesma mentira de
//                                 antes com outra cara.
//   · os 4 interruptores       -> foram para Comunicações > Canais, que é a aba
//                                 dos canais. Aqui ficou a linha que diz onde
//                                 eles estão: sumir com um controle sem dizer
//                                 para onde ele foi é pior do que deixá-lo.
//
// As abas (`activeTab`) e o visual delas não mudaram.
import React, { useMemo, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/suppliesSection/AlertsAndNotifications";
import {
  ConfigDeAlerta,
  ROTULO_ALERT_TYPE,
  ROTULO_EVENTO,
  ROTULO_SEVERIDADE,
  lerConfiguracoesDeAlerta,
} from "../../modals/AlertConfigModal";
import {
  ROTULO_CANAL,
  TEMPLATES_PADRAO,
  TemplateDeMensagem,
  extrairVariaveis,
  lerTemplatesGravados,
} from "./templatesPadrao";

interface AlertsAndNotificationsProps {
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
  // Os cinco de baixo continuam na interface porque o shell
  // (suppliesSection/index.tsx) os passa para todas as seções. Esta aba não usa
  // mais nenhum deles: o que dependia de `formData`/`handleInputChange` eram os
  // interruptores de notificação, que agora moram em Comunicações > Canais, e
  // `handleSave("notificationSettings")` não tinha case no switch — caía no
  // default e não gravava nada.
  isEditing?: boolean;
  formData?: any;
  handleInputChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  handleSave?: (objectType: any, payload?: any) => void;
  updateState?: (newState: any) => void;
}

const AlertsAndNotifications: React.FC<AlertsAndNotificationsProps> = ({
  decryptedInfoToken,
  handleOpenModal,
}) => {
  const [activeTab, setActiveTab] = useState("config");

  // O que a organização REALMENTE tem gravado. Vazio é vazio — e a tela diz
  // isso, em vez de encher com exemplo.
  const alertConfigurations: ConfigDeAlerta[] = useMemo(
    () => lerConfiguracoesDeAlerta(decryptedInfoToken),
    [decryptedInfoToken]
  );

  const templatesGravados: TemplateDeMensagem[] = useMemo(
    () => lerTemplatesGravados(decryptedInfoToken),
    [decryptedInfoToken]
  );

  // Semente: sem nenhum template gravado, a aba mostra os padrões do sistema —
  // marcados como padrão, um por um. Eles não estão em banco nenhum, e clicar
  // num deles abre o modal como CRIAÇÃO, que é o que ele é.
  const mostrandoPadrao = templatesGravados.length === 0;
  const templates: TemplateDeMensagem[] = mostrandoPadrao
    ? TEMPLATES_PADRAO
    : templatesGravados;

  const getSeverityBadge = (severity: string) => {
    // As chaves são as do formulário (AlertConfigModal): baixa/media/alta/
    // critica. O mock usava high/medium/low, vocabulário que não existia em
    // lugar nenhum além dele mesmo.
    const severityConfig: Record<
      string,
      { color: string; bg: string; label: string }
    > = {
      critica: { color: "#721c24", bg: "#f5c6cb", label: "Crítica" },
      alta: { color: "#dc3545", bg: "#f8d7da", label: "Alta" },
      media: { color: "#856404", bg: "#fff3cd", label: "Média" },
      baixa: { color: "#28a745", bg: "#d4edda", label: "Baixa" },
    };

    const config = severityConfig[severity] || severityConfig.media;

    return (
      <span
        style={{
          padding: "var(--sp-4) var(--sp-8)",
          borderRadius: "12px",
          fontSize: "var(--fs-2xs)",
          fontWeight: 600,
          backgroundColor: config.bg,
          color: config.color,
        }}
      >
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status: boolean) => (
    <span
      style={{
        padding: "var(--sp-4) var(--sp-8)",
        borderRadius: "12px",
        fontSize: "var(--fs-2xs)",
        fontWeight: 600,
        backgroundColor: status ? "#d4edda" : "#f8d7da",
        color: status ? "#155724" : "#721c24",
      }}
    >
      {status ? "Ativo" : "Inativo"}
    </span>
  );

  return (
    <AlfredoStyles.FieldsContainer>
      {/* Cabeçalho */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "var(--sp-32)",
          flexWrap: "wrap",
          gap: "var(--sp-20)",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "var(--fs-4xl)",
              fontWeight: 700,
              margin: "0 0 var(--sp-8) 0",
              color: "#1a1a1a",
            }}
          >
            Alertas e Notificações
          </h1>
          <p
            style={{
              color: "#6c757d",
              margin: 0,
              fontSize: "var(--fs-base)",
            }}
          >
            Configure alertas automáticos para gestão de suprimentos
          </p>
        </div>

        <AlfredoStyles.Button
          onClick={() => handleOpenModal("alertConfig")}
          style={{ minWidth: "calc(var(--u) * 140)" }}
        >
          <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
          Nova Configuração
        </AlfredoStyles.Button>
      </div>

      {/* Abas */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e1e5e9",
          marginBottom: "var(--sp-24)",
        }}
      >
        {[
          { id: "config", label: "Configurações", icon: "settings" },
          { id: "alerts", label: "Alertas Recentes", icon: "notifications" },
          { id: "templates", label: "Templates", icon: "description" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "var(--sp-12) var(--sp-24)",
              border: "none",
              backgroundColor: "transparent",
              borderBottom: `2px solid ${
                activeTab === tab.id ? "#0066cc" : "transparent"
              }`,
              color: activeTab === tab.id ? "#0066cc" : "#6c757d",
              fontWeight: activeTab === tab.id ? 600 : 400,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "var(--sp-8)",
              fontSize: "var(--fs-md)",
              transition: "all 0.2s ease",
            }}
          >
            <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-lg)" }}>
              {tab.icon}
            </AlfredoStyles.MaterialIcons>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conteúdo das Abas */}
      {activeTab === "config" && (
        <div>
          {/* Onde foram parar os interruptores de notificação */}
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #e1e5e9",
              borderRadius: "12px",
              padding: "var(--sp-24)",
              marginBottom: "var(--sp-24)",
            }}
          >
            <h3
              style={{
                margin: "0 0 var(--sp-20) 0",
                fontSize: "var(--fs-lg)",
                fontWeight: 600,
              }}
            >
              <AlfredoStyles.MaterialIcons
                style={{ fontSize: "var(--fs-xl)", marginRight: "var(--sp-8)" }}
              >
                notifications
              </AlfredoStyles.MaterialIcons>
              Configurações de Notificação
            </h3>

            <AlfredoStyles.Aviso $tom="atencao">
              <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-lg)" }}>
                swap_horiz
              </AlfredoStyles.MaterialIcons>
              <div>
                Email, WhatsApp, Push e Pedidos Automáticos mudaram de lugar:
                agora ficam em <strong>Comunicações &gt; Canais</strong>, junto
                com o resto da configuração de envio. Os interruptores que
                estavam aqui chamavam <code>handleSave("notificationSettings")</code>
                , que não tem tratamento no salvamento — eles pareciam ligar e
                desligar e nunca gravaram nada.
              </div>
            </AlfredoStyles.Aviso>
          </div>

          {/* Configurações de Alerta */}
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #e1e5e9",
              borderRadius: "12px",
              padding: "var(--sp-24)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "var(--sp-20)",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "var(--fs-lg)", fontWeight: 600 }}>
                <AlfredoStyles.MaterialIcons
                  style={{ fontSize: "var(--fs-xl)", marginRight: "var(--sp-8)" }}
                >
                  warning
                </AlfredoStyles.MaterialIcons>
                Configurações de Alerta
              </h3>
              <AlfredoStyles.SecondaryButton
                onClick={() => handleOpenModal("alertConfig")}
                style={{ padding: "var(--sp-8) var(--sp-16)", fontSize: "var(--fs-md)" }}
              >
                <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
                Nova Configuração
              </AlfredoStyles.SecondaryButton>
            </div>

            {alertConfigurations.length === 0 ? (
              <AlfredoStyles.CaixaVazia>
                <AlfredoStyles.CaixaVaziaTitulo>
                  Nenhum alerta configurado
                </AlfredoStyles.CaixaVaziaTitulo>
                <AlfredoStyles.CaixaVaziaTexto>
                  Os três alertas que apareciam aqui eram exemplo fixo no código,
                  não configuração desta organização. A lista passa a mostrar o
                  que estiver gravado na loja (ActSubsidiaryInfo tipo 23).
                </AlfredoStyles.CaixaVaziaTexto>
              </AlfredoStyles.CaixaVazia>
            ) : (
              <div style={{ display: "grid", gap: "var(--sp-12)" }}>
                {alertConfigurations.map((alert) => (
                  <div
                    key={alert.subsidiaryInfoUuid}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "var(--sp-16)",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e1e5e9",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f1f3f4";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}
                    onClick={() => handleOpenModal("alertConfig", alert)}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--sp-12)",
                          marginBottom: "var(--sp-8)",
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: "calc(var(--u) * 15)" }}>
                          {alert.nome}
                        </div>
                        {getStatusBadge(alert.ativo)}
                        {getSeverityBadge(alert.severidade)}
                      </div>
                      {alert.descricao && (
                        <div
                          style={{
                            color: "#6c757d",
                            fontSize: "var(--fs-md)",
                            marginBottom: "var(--sp-8)",
                          }}
                        >
                          {alert.descricao}
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          gap: "var(--sp-12)",
                          fontSize: "var(--fs-xs)",
                          color: "#6c757d",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>
                          <strong>Evento:</strong>{" "}
                          {ROTULO_EVENTO[alert.evento] || alert.evento || "—"}
                        </span>
                        <span>
                          <strong>Avisa por:</strong>{" "}
                          {ROTULO_ALERT_TYPE[alert.alertType] || alert.alertType}
                        </span>
                        <span>
                          <strong>Loja:</strong> {alert.subsidiaryName || "—"}
                        </span>
                        <span>
                          <strong>Severidade:</strong>{" "}
                          {ROTULO_SEVERIDADE[alert.severidade] ||
                            alert.severidade}
                        </span>
                      </div>
                    </div>
                    <AlfredoStyles.MaterialIcons
                      style={{ color: "#6c757d", cursor: "pointer" }}
                    >
                      chevron_right
                    </AlfredoStyles.MaterialIcons>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "alerts" && (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #e1e5e9",
            borderRadius: "12px",
            padding: "var(--sp-24)",
          }}
        >
          <h3
            style={{ margin: "0 0 var(--sp-20) 0", fontSize: "var(--fs-lg)", fontWeight: 600 }}
          >
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "var(--fs-xl)", marginRight: "var(--sp-8)" }}
            >
              history
            </AlfredoStyles.MaterialIcons>
            Alertas Recentes
          </h3>

          {/* Lista honestamente vazia. Os três alertas de antes (com data de
              2024, "Farinha de Trigo" e um botão "Resolver" que só fazia
              console.log) eram literais no JSX: davam a entender que o sistema
              já dispara e registra alerta. Ele não dispara. */}
          <AlfredoStyles.CaixaVazia>
            <AlfredoStyles.CaixaVaziaTitulo>
              Nenhum alerta registrado
            </AlfredoStyles.CaixaVaziaTitulo>
            <AlfredoStyles.CaixaVaziaTexto>
              O histórico de alertas depende de uma entidade "Alerta" que ainda
              não existe: não há tabela onde gravar o disparo, nem rota que
              devolva a lista, nem quem dispare. Enquanto isso não existir, esta
              tela não tem o que mostrar aqui — e preferimos dizer isso a
              preencher com exemplo. Configure o QUE deve disparar na aba
              "Configurações".
            </AlfredoStyles.CaixaVaziaTexto>
          </AlfredoStyles.CaixaVazia>
        </div>
      )}

      {activeTab === "templates" && (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #e1e5e9",
            borderRadius: "12px",
            padding: "var(--sp-24)",
          }}
        >
          <h3
            style={{ margin: "0 0 var(--sp-20) 0", fontSize: "var(--fs-lg)", fontWeight: 600 }}
          >
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "var(--fs-xl)", marginRight: "var(--sp-8)" }}
            >
              description
            </AlfredoStyles.MaterialIcons>
            Templates de Mensagem
          </h3>

          {mostrandoPadrao && (
            <div style={{ marginBottom: "var(--sp-20)" }}>
              <AlfredoStyles.Aviso $tom="atencao">
                <AlfredoStyles.MaterialIcons
                  style={{ fontSize: "var(--fs-lg)" }}
                >
                  info
                </AlfredoStyles.MaterialIcons>
                <div>
                  Esta organização ainda não gravou nenhum template. O que está
                  abaixo é o <strong>padrão do sistema</strong> (semente do
                  código), não configuração salva: abrir um deles e salvar cria a
                  primeira versão da organização.
                </div>
              </AlfredoStyles.Aviso>
            </div>
          )}

          <div style={{ display: "grid", gap: "var(--sp-16)" }}>
            {templates.map((template, indice) => {
              // As variáveis são DERIVADAS do texto, sempre. Guardar uma lista
              // paralela é como o dado antigo divergia do conteúdo na primeira
              // edição — e a mensagem saía com "{produto}" cru.
              const variaveis = extrairVariaveis(template.conteudo);

              return (
                <div
                  key={template.organizationInfoUuid ?? `padrao-${indice}`}
                  style={{
                    padding: "var(--sp-20)",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e1e5e9",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f1f3f4";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onClick={() => handleOpenModal("messageTemplate", template)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "var(--sp-12)",
                      gap: "var(--sp-12)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--sp-8)",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: "var(--fs-base)" }}>
                        {template.nome}
                      </div>
                      {template.padraoDoSistema && (
                        <AlfredoStyles.SeloPadrao>
                          padrão do sistema — não gravado
                        </AlfredoStyles.SeloPadrao>
                      )}
                    </div>
                    <span
                      style={{
                        padding: "var(--sp-4) var(--sp-8)",
                        borderRadius: "12px",
                        fontSize: "var(--fs-2xs)",
                        fontWeight: 600,
                        backgroundColor:
                          template.canal === "whatsapp"
                            ? "#25D366"
                            : template.canal === "sms"
                            ? "#6c757d"
                            : "#0066cc",
                        color: "white",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ROTULO_CANAL[template.canal]}
                    </span>
                  </div>
                  <div
                    style={{
                      backgroundColor: "white",
                      padding: "var(--sp-12)",
                      borderRadius: "6px",
                      border: "1px solid #e1e5e9",
                      fontSize: "var(--fs-md)",
                      color: "#495057",
                      whiteSpace: "pre-wrap",
                      marginBottom: "var(--sp-12)",
                      fontFamily: "monospace",
                    }}
                  >
                    {template.conteudo}
                  </div>
                  <div style={{ display: "flex", gap: "var(--sp-8)", flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontSize: "var(--fs-xs)",
                        color: "#6c757d",
                        fontWeight: 600,
                      }}
                    >
                      Variáveis:
                    </span>
                    {variaveis.length === 0 ? (
                      <span style={{ fontSize: "var(--fs-xs)", color: "#6c757d" }}>
                        nenhuma
                      </span>
                    ) : (
                      variaveis.map((variable) => (
                        <code
                          key={variable}
                          style={{
                            padding: "var(--sp-2) var(--sp-6)",
                            backgroundColor: "#e9ecef",
                            borderRadius: "4px",
                            fontSize: "var(--fs-2xs)",
                            color: "#495057",
                            fontFamily: "monospace",
                          }}
                        >
                          {`{${variable}}`}
                        </code>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              backgroundColor: "#e7f3ff",
              border: "1px solid #b3d9ff",
              borderRadius: "8px",
              padding: "var(--sp-16)",
              marginTop: "var(--sp-20)",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "flex-start", gap: "var(--sp-12)" }}
            >
              <AlfredoStyles.MaterialIcons
                style={{ color: "#0066cc", fontSize: "var(--fs-xl)" }}
              >
                info
              </AlfredoStyles.MaterialIcons>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    color: "#0066cc",
                    marginBottom: "var(--sp-4)",
                  }}
                >
                  Como usar templates
                </div>
                <div style={{ fontSize: "var(--fs-md)", color: "#0066cc" }}>
                  Use as variáveis entre chaves para personalizar
                  automaticamente suas mensagens. A lista de variáveis é lida do
                  próprio texto — não existe lista separada para desencontrar do
                  conteúdo. Quem envia a mensagem substitui o que souber
                  preencher.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default AlertsAndNotifications;
